import React, { useEffect, useMemo, useState } from "react";
import orendaService from "../../services/orenda.service";
import { parseKycSchemaResponse } from "./orendaApplicationHelpers";
import { orendaUserManager } from "./orendaUserManager";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";

// KYC_INTERNAL onboarding step. Fetches the schema from getkycschemaorenda,
// renders it as a multi-step wizard driven by schema.step-N field arrays,
// evaluates hide_if / required_if_not conditionals + regex validation on
// visible fields, then submits with submitkycdataorenda.

// Conditional evaluation
// `condition` shape: { key: "foo", value: ["a", "b"] }
// hide_if:         hide when answers[key] ∈ value[]
// required_if_not: required when answers[key] ∉ value[]
const answerIn = (answer, values) => {
  if (!Array.isArray(values)) return false;
  if (answer == null) return false;
  return values.includes(String(answer));
};
const isFieldHidden = (field, answers) => {
  const cond = field.hide_if;
  if (!cond || !cond.key) return false;
  return answerIn(answers[cond.key], cond.value);
};
const isFieldRequired = (field, answers) => {
  if (field.required) return true;
  const cond = field.required_if_not;
  if (!cond || !cond.key) return false;
  return !answerIn(answers[cond.key], cond.value);
};

const validateField = (field, value, answers) => {
  if (isFieldHidden(field, answers)) return null;
  const required = isFieldRequired(field, answers);
  const empty =
    value == null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "boolean" && value === false && required);
  if (required && empty) return "Required.";
  if (!empty && field.validation?.regex) {
    try {
      const re = new RegExp(field.validation.regex);
      if (!re.test(String(value))) {
        return field.validation.errorMessage || "Invalid value.";
      }
    } catch {
      // malformed regex from server — don't block the user
    }
  }
  return null;
};

const FieldRenderer = ({ field, value, onChange, error }) => {
  const label = field.label || field.key;
  const type = (field.input_type || "text").toLowerCase();
  const errCls = error ? "orenda-field--error" : "";

  let control;
  switch (type) {
    case "select":
    case "dropdown":
      control = (
        <select
          className={errCls}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select —</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label || opt.value}
            </option>
          ))}
        </select>
      );
      break;
    case "radio":
      control = (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(field.options || []).map((opt) => (
            <label key={opt.value} className="orenda-inline-check">
              <input
                type="radio"
                name={field.key}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => onChange(e.target.value)}
              />
              <span>{opt.label || opt.value}</span>
            </label>
          ))}
        </div>
      );
      break;
    case "checkbox":
      return (
        <div className="orenda-field">
          <label className="orenda-inline-check">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span>
              {label}
              {field.required && (
                <span style={{ color: "#a3272a", marginLeft: 4 }}>*</span>
              )}
            </span>
          </label>
          {error && <div className="orenda-field-error">{error}</div>}
        </div>
      );
    case "textarea":
      control = (
        <textarea
          className={errCls}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
        />
      );
      break;
    case "date":
      control = (
        <input
          className={errCls}
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "number":
      control = (
        <input
          className={errCls}
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "email":
      control = (
        <input
          className={errCls}
          type="email"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="email"
        />
      );
      break;
    case "tel":
    case "phone":
      control = (
        <input
          className={errCls}
          type="tel"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="tel"
        />
      );
      break;
    case "text":
    default:
      control = (
        <input
          className={errCls}
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }

  return (
    <div className="orenda-field">
      <label>
        {label}
        {field.required && (
          <span style={{ color: "#a3272a", marginLeft: 4 }}>*</span>
        )}
      </label>
      {control}
      {error && <div className="orenda-field-error">{error}</div>}
    </div>
  );
};

// Pulls kycQuestionnaireErrors[] out of the raw viewapplication payload so we
// can show the applicant which fields were rejected on the previous submission.
const extractQuestionnaireErrors = (application, raw) => {
  const fromApplication = application?.state?.kycQuestionnaireErrors;
  if (Array.isArray(fromApplication)) return fromApplication;
  const fromRaw = raw?.application?.state?.kycQuestionnaireErrors;
  if (Array.isArray(fromRaw)) return fromRaw;
  return [];
};

// Pulls any previously-saved KYC answers out of application.kycData and
// flattens them into { fieldKey: value } for our form state. Orenda v2 may
// return kycData either flat or step-grouped (mirroring the submit shape).
const extractKycSeedAnswers = (application, raw) => {
  const kycData =
    application?.kycData ??
    raw?.application?.kycData ??
    null;
  if (!kycData || typeof kycData !== "object") return {};

  const out = {};
  for (const [key, value] of Object.entries(kycData)) {
    // Step-grouped array shape: { "step-1": [{key, data}, ...] }
    if (/^step-\d+$/i.test(key) && Array.isArray(value)) {
      for (const pair of value) {
        if (pair && typeof pair === "object" && pair.key != null) {
          out[pair.key] =
            pair.data != null ? String(pair.data)
            : pair.value != null ? String(pair.value)
            : "";
        }
      }
      continue;
    }
    // Step-grouped object shape: { "step-1": { fieldKey: value } }
    if (/^step-\d+$/i.test(key) && value && typeof value === "object") {
      for (const [k, v] of Object.entries(value)) {
        if (v != null) out[k] = String(v);
      }
      continue;
    }
    // Flat shape: { fieldKey: value }
    if (value != null && (typeof value === "string" || typeof value === "number" || typeof value === "boolean")) {
      out[key] = String(value);
    }
  }
  return out;
};

const OrendaKycInternal = ({ application, status, raw, onAdvance }) => {
  const previousErrors = extractQuestionnaireErrors(application, raw);
  // v2 Orenda keeps currentStep === "KYC_FORM" even when a previous submission
  // had errors; the only signal is the presence of kycQuestionnaireErrors in
  // application.state. (The v1 KYC_INTERNAL_VALIDATION_ERRORS step still
  // triggers the banner too for backwards compatibility.)
  const isRetry =
    previousErrors.length > 0 ||
    status?.currentStep === "KYC_INTERNAL_VALIDATION_ERRORS";
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [parsed, setParsed] = useState(null);
  // Seed answers from any prior submission the backend has on file. We only
  // compute this once at mount — subsequent re-fetches from upstream shouldn't
  // overwrite in-progress typing.
  const [answers, setAnswers] = useState(() =>
    extractKycSeedAnswers(application, raw)
  );
  const [errors, setErrors] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let alive = true;
    orendaService
      .getKycSchemaOrenda()
      .then((response) => {
        if (!alive) return;
        setParsed(parseKycSchemaResponse(response));
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err?.response?.data?.errorMessage || err?.message || String(err)
        );
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const steps = parsed?.steps || [];
  const currentStep = steps[stepIndex];

  const visibleFields = useMemo(() => {
    if (!currentStep) return [];
    return currentStep.fields.filter((f) => !isFieldHidden(f, answers));
  }, [currentStep, answers]);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateStep = () => {
    const next = {};
    for (const field of visibleFields) {
      const err = validateField(field, answers[field.key], answers);
      if (err) next[field.key] = err;
    }
    setErrors((prev) => ({ ...prev, ...next }));
    return Object.keys(next).length === 0;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const submit = async () => {
    if (!validateStep()) return;

    // Orenda expects answers grouped by step, arrayed { key, data }.
    const kycanswers = {};
    for (const step of steps) {
      const pairs = [];
      for (const field of step.fields) {
        if (isFieldHidden(field, answers)) continue;
        const val = answers[field.key];
        if (val === undefined || val === null) continue;
        pairs.push({
          key: field.key,
          data: typeof val === "string" ? val : String(val),
        });
      }
      if (pairs.length > 0) kycanswers[step.key] = pairs;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSessionExpired(false);
    try {
      await orendaService.submitKycDataOrenda({
        kycanswers,
        applicationId: application?.applicationId,
      });
      onAdvance?.();
    } catch (err) {
      if (err?.code === "SSO_SESSION_EXPIRED") {
        setSessionExpired(true);
      } else {
        setSubmitError(
          err?.response?.data?.errorMessage || err?.message || String(err)
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reAuthenticate = () => {
    orendaUserManager.signinRedirect({
      extraQueryParams: {
        identity_provider: orendaOidcConfig.identity_provider,
        prompt: "login",
      },
    });
  };

  if (loading) {
    return <div className="orenda-card">Loading your information form…</div>;
  }
  if (loadError) {
    return (
      <div className="orenda-alert orenda-alert--warn">
        <strong>Could not load form.</strong>
        <div>{loadError}</div>
      </div>
    );
  }
  if (steps.length === 0) {
    return (
      <div className="orenda-alert orenda-alert--warn">
        No form fields were provided. Please contact support.
      </div>
    );
  }

  const isLastStep = stepIndex === steps.length - 1;

  return (
    <>
      <h2 className="orenda-heading">{currentStep.name}</h2>
      <p className="orenda-subheading">
        {currentStep.description ||
          "Please fill in your details. All required fields are marked with an asterisk."}
      </p>

      {isRetry && (
        <div className="orenda-alert orenda-alert--warn">
          <strong>Your previous submission had errors.</strong>
          <div style={{ marginTop: 4 }}>Please review and re-submit.</div>
          {previousErrors.length > 0 && (
            <ul style={{ margin: "6px 0 0 20px", padding: 0, fontSize: 13 }}>
              {previousErrors.map((err, i) => (
                <li key={i}>{err.message || err.step}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="orenda-card">
        <div className="orenda-eyebrow">
          Section {stepIndex + 1} of {steps.length}
        </div>
        {visibleFields.length === 0 && (
          <div style={{ color: "#6b6f78", fontSize: 14 }}>
            No fields to show for this section.
          </div>
        )}
        {visibleFields.map((field) => (
          <FieldRenderer
            key={field.key}
            field={field}
            value={answers[field.key]}
            onChange={(v) => setAnswer(field.key, v)}
            error={errors[field.key]}
          />
        ))}
      </div>

      {sessionExpired && (
        <div className="orenda-alert orenda-alert--warn">
          <strong>Your session expired.</strong>
          <div style={{ marginTop: 4, marginBottom: 8 }}>
            Sign in again to submit. Your answers on this section are still
            filled in.
          </div>
          <button onClick={reAuthenticate} className="orenda-btn-primary">
            Sign in again
          </button>
        </div>
      )}

      {submitError && (
        <div className="orenda-alert orenda-alert--warn">
          <strong>Submission failed.</strong>
          <div>{submitError}</div>
        </div>
      )}

      <div className="orenda-actions">
        <button
          onClick={goBack}
          disabled={stepIndex === 0}
          className="orenda-btn-ghost"
        >
          ← Back
        </button>
        {isLastStep ? (
          <button
            onClick={submit}
            disabled={submitting}
            className="orenda-btn-primary"
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        ) : (
          <button onClick={goNext} className="orenda-btn-primary">
            Next →
          </button>
        )}
      </div>
    </>
  );
};

export default OrendaKycInternal;
